// Browser client for the NESO Carbon Intensity API (api.carbonintensity.org.uk).
//
// The API is keyless and CORS-enabled, so the whole live "Now" view runs from the
// page with no Dwellow backend. The data only changes every 30-minute settlement
// period, so we cache each response until the current period rolls over — being a
// good citizen of an API with an undocumented rate limit.
//
// Times are UTC throughout; callers convert to the reader's local clock for
// "midday solar" / "evening peak" copy.

const DEFAULT_BASE = 'https://api.carbonintensity.org.uk';

// ISO8601 to the minute with a Z, e.g. 2026-07-10T11:00Z — the shape the API wants.
export function toApiTime(date) {
    return date.toISOString().slice(0, 16) + 'Z';
}

// Start of the current 30-minute settlement period, used as the cache key so a
// single period is only fetched once across all panels.
export function settlementPeriodStart(now = new Date()) {
    const d = new Date(now.getTime());
    d.setUTCSeconds(0, 0);
    d.setUTCMinutes(d.getUTCMinutes() < 30 ? 0 : 30);
    return d;
}

export function createCarbonIntensityClient({ baseUrl = DEFAULT_BASE, fetchImpl } = {}) {
    const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null);
    if (!doFetch) throw new Error('No fetch implementation available');

    const cache = new Map(); // path -> { periodKey, promise }

    async function getJson(path) {
        const periodKey = settlementPeriodStart().toISOString();
        const hit = cache.get(path);
        if (hit && hit.periodKey === periodKey) return hit.promise;

        const promise = (async () => {
            const res = await doFetch(baseUrl + path, { headers: { Accept: 'application/json' } });
            if (!res.ok) throw new Error(`carbon_intensity_${res.status}`);
            return res.json();
        })().catch(err => {
            // Don't cache failures — let the next call retry.
            if (cache.get(path)?.promise === promise) cache.delete(path);
            throw err;
        });

        cache.set(path, { periodKey, promise });
        return promise;
    }

    return {
        // Current national carbon intensity: { forecast, actual, index }.
        async intensityNow() {
            const data = await getJson('/intensity');
            return data?.data?.[0]?.intensity || null;
        },

        // Current generation mix as an array of { fuel, perc }.
        async generationNow() {
            const data = await getJson('/generation');
            return data?.data?.generationmix || [];
        },

        // The last 24 hours of half-hourly actual intensity, for the trend line.
        async intensityPast24h(now = new Date()) {
            const from = toApiTime(new Date(now.getTime() - 24 * 60 * 60 * 1000));
            const data = await getJson(`/intensity/${from}/pt24h`);
            return data?.data || [];
        },

        // Today's 48 half-hourly periods (actual where settled, forecast after),
        // for the "through the day" curve: 00:00 → 24:00 with a clean now split.
        async intensityToday() {
            const data = await getJson('/intensity/date');
            return data?.data || [];
        },

        // The next 24 hours of half-hourly forecast intensity, for the green window.
        async intensityForecast24h(now = new Date()) {
            const from = toApiTime(settlementPeriodStart(now));
            const data = await getJson(`/intensity/${from}/fw24h`);
            return data?.data || [];
        },

        // Today vs yesterday average intensity via daily (24h block) stats.
        async dailyAverages(now = new Date()) {
            const from = toApiTime(new Date(now.getTime() - 48 * 60 * 60 * 1000));
            const to = toApiTime(now);
            const data = await getJson(`/intensity/stats/${from}/${to}/24`);
            const blocks = data?.data || [];
            const averages = blocks
                .map(block => Number(block?.intensity?.average))
                .filter(Number.isFinite);
            return {
                yesterdayAvg: averages.length >= 2 ? averages[averages.length - 2] : null,
                todayAvg: averages.length >= 1 ? averages[averages.length - 1] : null,
            };
        },
    };
}
